import React, { useEffect, useState, useMemo, useRef, useCallback, lazy } from 'react';
import {
    Box,
    List,
    ListItemButton,
    Avatar,
    Typography,
    AppBar,
    Toolbar,
    useMediaQuery,
    useTheme,
    Badge,
    IconButton,
    Select,
    MenuItem,
} from '@mui/material';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import AddCommentIcon from '@mui/icons-material/AddComment'; // Иконка для нового чата
import HubIcon from '@mui/icons-material/Hub';
import GroupIcon from '@mui/icons-material/Group'; // Иконка группы
import GroupAddIcon from '@mui/icons-material/GroupAdd'; // Иконка для кнопки
import ChatPage from './ChatPage';
import SettingsPage from './SettingsPage';
import axiosClient from '../api/axiosClient';
import { Conversation} from '../types';
import { useWebSocket } from '../hooks/useWebSocket';
import {
    getMessageConversationId,
    getMessageText,
    isIncomingMessage,
    isSystemEvent,
    messageEventKey,
} from '../api/wsMessage';
import { UserSelector } from '../components/UserSelector'; // Импорт нового компонента
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import {  styled, Theme  } from '@mui/material/styles';
import { CreateGroupDialog } from '../components/ContactList/CreateGroupDialog';
import SearchDialog from './SearchDialog';
import { formatChatListTime } from '../components/Chat/chatDate';
import { channelLabel } from '../components/Chat/clientChannels';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';


const CalendarModule = lazy(() => import('../modules/Calendar/CalendarModule'));

// Названия организаций приходят с бэкенда с эмодзи-префиксом («🏢 КОМПАНИЯ»,
// «🔒 МОЙ ЛИЧНЫЙ ГРАФИК»). В панели с иконкой префикс лишний.
const displayOrgName = (name: string): string => name.replace(/^[^0-9A-Za-zА-Яа-яЁё]+/, '').trim() || name;


const StyledBadge = styled(Badge)(({ theme }: { theme: Theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: '#44b700',
        color: '#44b700',
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        width: 10,
        height: 10,
        borderRadius: '50%',
    },
}));

// 2. Ширина панели со списком чатов. На десктопе её можно менять
// перетаскиванием разделителя (как в Telegram Desktop); значение запоминаем,
// чтобы после перезагрузки панель осталась такой же ширины.
const CHAT_LIST_WIDTH_KEY = 'orion_chat_list_width';
const CHAT_LIST_WIDTH_DEFAULT = 350;
const CHAT_LIST_WIDTH_MIN = 260;
const CHAT_LIST_WIDTH_MAX = 640;

const getInitialChatListWidth = (): number => {
    const saved = Number(localStorage.getItem(CHAT_LIST_WIDTH_KEY));
    if (!Number.isFinite(saved) || saved < CHAT_LIST_WIDTH_MIN || saved > CHAT_LIST_WIDTH_MAX) {
        return CHAT_LIST_WIDTH_DEFAULT;
    }
    return saved;
};

const DashboardPage: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();

    const activeChatId = location.pathname.split('/').pop();
    const isChatOpen = location.pathname.includes('/chat/');
    const isCalendarOpen = location.pathname.includes('/calendar');
    const isSettingsOpen = location.pathname.includes('/settings');

    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [isUserSelectorOpen, setIsUserSelectorOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const { user } = useAuth();
    // Выбранная организация на уровне приложения: '' — все организации / личное.
    const { orgId, organizations, setOrg } = useOrg();
    // В разделе чатов личный график не нужен: показываем только командные организации.
    const chatOrganizations = useMemo(
        () => organizations.filter((org) => org.type !== 'personal'),
        [organizations]
    );
    // Если выбран личный график, фильтрацию чатов не меняем (orgId как есть),
    // но в этом селекте показываем «Все организации» — личной организации в нём нет.
    const selectedChatOrgId = chatOrganizations.some((org) => org.id === orgId) ? orgId : '';
    const { latestMessage } = useWebSocket(undefined, user?.id);

    // "память" для уже обработанных событий сокета
    // Ключ составной: у сообщения это его ID, у системного события (статус)
    // ID нет вовсе. Раньше сравнивался только `id`, поэтому события статуса
    // «глушили» друг друга и Presence замирал.
    const lastProcessedMessageKey = useRef<string | null>(null);

    // Загрузка списка бесед: зависит только от выбранной организации, поэтому
    // мемоизируем — иначе эффекты ниже перезапускались бы на каждом рендере.
    const fetchConversations = useCallback(async () => {
        try {
            // '' = «все организации / личное»: параметр mode не отправляем вовсе.
            const res = await axiosClient.get<Conversation[]>('/conversations', {
                params: { mode: orgId || undefined },
            });
            setConversations(res.data);
        } catch (err) {
            console.error('Failed to fetch conversations', err);
        }
    }, [orgId]);

    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);


    useEffect(() => {
        if (!latestMessage) return;

        const eventKey = messageEventKey(latestMessage);

        // Если событие пустое или уже было обработано - ВЫХОДИМ
        if (!eventKey || eventKey === lastProcessedMessageKey.current) {
            return;
        }

        // Запоминаем ключ текущего события как обработанного
        lastProcessedMessageKey.current = eventKey;

        // 🟢 1. ЛОГИКА ОНЛАЙНА
        if (isSystemEvent(latestMessage)) {
            if (latestMessage.event !== 'userStatusChanged') return;

            const { userId, status, lastSeen } = latestMessage;

            setConversations(prev => prev.map(conv => {
                // Проверяем, относится ли статус к контакту в этом чате
                if (conv.contact && conv.contact.id === userId) {
                    return {
                        ...conv,
                        contact: {
                            ...conv.contact,
                            isOnline: status === 'online',
                            lastSeen: lastSeen || new Date().toISOString()
                        }
                    };
                }
                return conv;
            }));
            return; // Выходим, чтобы не обрабатывать статус как сообщение
        }

        //  2. ЛОГИКА ПЕРЕМЕЩЕНИЯ ЧАТА ВВЕРХ (пришло новое сообщение)
        {
            const msgConvId = getMessageConversationId(latestMessage);
            if (!msgConvId) return;

            // Направление берём из сообщения: сервер проставляет его всегда.
            // Раньше автор сравнивался с текущим пользователем, и если
            // `senderId` не приходил (например, Telegram), собственные
            // сообщения считались входящими и накручивали счётчик.
            const isIncoming = isIncomingMessage(latestMessage, user?.id);

            // 🛡️ Проверка: открыт ли чат сейчас
            const isCurrentlyOpen = String(activeChatId) === String(msgConvId);

            // Чат открыт и сообщение входящее: подтверждаем прочтение на
            // сервере, иначе после перезагрузки бейдж вернётся.
            if (isIncoming && isCurrentlyOpen) {
                axiosClient.post(`/conversations/${msgConvId}/read`).catch(() => {});
            }

            setConversations(prev => {
                const existingIndex = prev.findIndex(c => c.id === msgConvId);

                if (existingIndex === -1) {
                    // ЧАТА НЕТ (например, новая группа): обновляем список с сервера
                    fetchConversations();
                    return prev;
                }

                // ЧАТ НАЙДЕН: Обновляем превью и ПУШИМ В НАЧАЛО СПИСКА
                const existingChat = prev[existingIndex];

                const updatedChat: Conversation = {
                    ...existingChat,
                    lastMessageText: getMessageText(latestMessage) || '📷 Фото',
                    lastMessageAt: latestMessage.sentAt || new Date().toISOString(),
                    // Направление нужно, чтобы сразу показать «Ждут ответа»,
                    // не перезапрашивая список после каждого сообщения.
                    lastMessageDirection: isIncoming ? 'incoming' : 'outgoing',
                    // СЧЕТЧИК:
                    // не входящее (наше) -> не трогаем
                    // входящее и чат открыт -> 0
                    // входящее и чат закрыт -> +1
                    unreadCount: !isIncoming
                        ? existingChat.unreadCount
                        : (isCurrentlyOpen ? 0 : existingChat.unreadCount + 1)
                };

                // Убираем старую позицию и ставим обновленный чат первым
                const otherConversations = prev.filter(c => c.id !== msgConvId);
                return [updatedChat, ...otherConversations];
            });
        }
    }, [latestMessage, activeChatId, user?.id, fetchConversations]);

    // Счётчик непрочитанных сбрасывается при любом открытии чата, а не только
    // по клику в списке: иначе переход по прямой ссылке, кнопка «Назад» или
    // возврат из поиска оставляли бейдж висеть.
    // Отметку «прочитано» на сервере делает только useChat (POST /read при
    // монтировании ChatPage). Дублировать запрос из списка не нужно: каждый
    // лишний POST — это лишняя запись в БД и занятый php-fpm воркер, из-за
    // чего чат открывается медленнее. Здесь гасим бейдж только локально.
    useEffect(() => {
        if (!isChatOpen || !activeChatId) return;

        setConversations(prev => prev.map(conv =>
            conv.id === activeChatId && conv.unreadCount > 0
                ? { ...conv, unreadCount: 0 }
                : conv
        ));
    }, [activeChatId, isChatOpen]);


    // Метод создания внутреннего чата
    const handleStartInternalChat = async (userId: string) => {
        try {
            const res = await axiosClient.post('/conversations/internal', { userId });
            setIsUserSelectorOpen(false);
            await fetchConversations();
            navigate(`/dashboard/chat/${res.data.id}`);
        } catch (err) {
            console.error('Failed to start internal chat', err);
        }
    };


    const getChannelIcon = (type: string | undefined) => {

        const t = type?.toLowerCase();
        if (t === 'whatsapp') return <WhatsAppIcon sx={{ fontSize: 14, color: '#25D366' }} />;
        if (t === 'telegram') return <TelegramIcon sx={{ fontSize: 14, color: '#24A1DE' }} />;
        if (t === 'orion') return <HubIcon sx={{ fontSize: 14, color: '#666' }} />;
        return null;
    };

    // Канал переписки подписью: иконки есть только у WhatsApp и Telegram,
    // поэтому в списке чатов нужен ещё и текст — откуда пришёл клиент.
    // У переписок, созданных внутри контура, канал «orion». Но у групповых
    // бесед «orion» — это не канал, а тип беседы: без отдельной метки все
    // группы подписывались «ORION» и выглядели как личные чаты сотрудников.
    // Поэтому у групп подпись «Группа», а канал (если беседа пришла из
    // Telegram/WhatsApp) остаётся иконкой слева.
    const getChannelLabel = (conv: Conversation) =>
        conv.isGroup ? 'Группа' : (channelLabel(conv.type) || 'ORION');

    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);

    // Функция, которая вызывается, когда группа создана на бэкенде
    const handleGroupCreated = (newGroup: any) => {
        // Обновляем список чатов, чтобы увидеть новую группу
        fetchConversations(); 
        // Опционально: сразу переходим в этот чат
        navigate(`/dashboard/chat/${newGroup.id}`);
    };


    // функция обработки клика внутри компонента
    // Счётчик и отметку «прочитано» на бэкенде сбрасывает эффект на
    // `activeChatId` — он же срабатывает при переходе по ссылке и по «Назад».
    const handleChatClick = (convId: string) => {
        navigate(`/dashboard/chat/${convId}`);
    };


    // Ширина панели бесед (десктоп) и состояние перетаскивания разделителя.
    const [chatListWidth, setChatListWidth] = useState<number>(getInitialChatListWidth);
    const [isResizingChatList, setIsResizingChatList] = useState(false);
    const resizeStartRef = useRef({ x: 0, width: CHAT_LIST_WIDTH_DEFAULT });

    const handleResizerMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        resizeStartRef.current = { x: event.clientX, width: chatListWidth };
        setIsResizingChatList(true);
    };

    // Слушатели на document: курсор не «теряется», если увести мышь за пределы
    // полосы разделителя, а на время перетаскивания отключаем выделение текста.
    useEffect(() => {
        if (!isResizingChatList) return;

        const handleMouseMove = (event: MouseEvent) => {
            const { x, width } = resizeStartRef.current;
            const next = width + (event.clientX - x);
            setChatListWidth(Math.min(CHAT_LIST_WIDTH_MAX, Math.max(CHAT_LIST_WIDTH_MIN, next)));
        };
        const handleMouseUp = () => setIsResizingChatList(false);

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        const { body } = document;
        const previousUserSelect = body.style.userSelect;
        const previousCursor = body.style.cursor;
        body.style.userSelect = 'none';
        body.style.cursor = 'col-resize';

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            body.style.userSelect = previousUserSelect;
            body.style.cursor = previousCursor;
        };
    }, [isResizingChatList]);

    // Запоминаем выбранную ширину между сессиями.
    useEffect(() => {
        localStorage.setItem(CHAT_LIST_WIDTH_KEY, String(chatListWidth));
    }, [chatListWidth]);



    return (
        <Box
            sx={{
                display: 'flex',
                // Высота плашки офлайн-режима: раскладка не прячется под ней.
                height: 'calc(100dvh - var(--orion-offline-banner-height, 0px))',
                width: '100vw',
                position: 'fixed',
                top: 'var(--orion-offline-banner-height, 0px)',
                left: 0,
                overflow: 'hidden',
            }}
        >
            {(!isMobile || (!isChatOpen && !isCalendarOpen && !isSettingsOpen)) && (
                <Box
                    sx={{
                        width: isMobile ? '100%' : chatListWidth,
                        flexShrink: 0,
                        borderRight: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'white',
 
                    }}
                >
                    <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
                        <Toolbar sx={{ justifyContent: 'space-between' }}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                ORION
                            </Typography>

                            <Box sx={{ flexShrink: 0 }}>
                                <IconButton
                                    color="primary"
                                    onClick={() => setIsSearchOpen(true)}
                                    title="Поиск"
                                    aria-label="Поиск"
                                >
                                    <SearchIcon />
                                </IconButton>
                                <IconButton color="primary" onClick={() => navigate('/dashboard/calendar')} title="Календарь">
                                    <CalendarMonthIcon />
                                </IconButton>
                                <IconButton color="primary" onClick={() => setIsGroupModalOpen(true)}>
                                    <GroupAddIcon />
                                </IconButton>
                                <IconButton color="primary" onClick={() => setIsUserSelectorOpen(true)}>
                                    <AddCommentIcon />
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    onClick={() => navigate('/dashboard/settings')}
                                    title="Настройки"
                                    sx={{ ml: 0.5 }}
                                >
                                    <SettingsIcon />
                                </IconButton>
                            </Box>
                        </Toolbar>
                    </AppBar>

                    {/* Панель выбора организации для чатов: отдельно от шапки,
                        на всю ширину панели бесед. */}
                    {chatOrganizations.length > 0 && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: 2,
                                py: 1.5,
                                bgcolor: '#F7F8FA',
                                borderBottom: 1,
                                borderColor: 'divider',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '0.68rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                    mb: 0.6,
                                }}
                            >
                                Организация
                            </Typography>
                            <Select
                                value={selectedChatOrgId}
                                onChange={(e) => {
                                    void setOrg(e.target.value);
                                }}
                                size="small"
                                variant="outlined"
                                fullWidth
                                inputProps={{ 'aria-label': 'Организация' }}
                                renderValue={(value: string) => {
                                    const selected = chatOrganizations.find((org) => org.id === value);
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                            <BusinessIcon sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }} />
                                            <Box
                                                component="span"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {selected ? displayOrgName(selected.name) : 'Все организации'}
                                            </Box>
                                        </Box>
                                    );
                                }}
                                sx={{
                                    bgcolor: 'background.paper',
                                    borderRadius: '10px',
                                    fontSize: '0.85rem',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                                    '& .MuiSelect-select': { py: 0.75 },
                                }}
                            >
                                <MenuItem value="">Все организации</MenuItem>
                                {chatOrganizations.map((org) => (
                                    <MenuItem key={org.id} value={org.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                            <BusinessIcon sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }} />
                                            <Box
                                                component="span"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {displayOrgName(org.name)}
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}

                    <List sx={{ 
                                flexGrow: 1, 
                                overflowY: 'auto', 
                                p: 0, 
                                WebkitOverflowScrolling: 'touch',                        
                                // МАГИЯ ПРОТИВ ЗАМОРОЗКИ:
                                 contain: 'content',           // Изолирует рендер списка от остального DOM
                                 willChange: 'scroll-position', // Готовит GPU к прокрутке заранее 
                            }}>
                        {conversations.map((conv) => {
                            // «Ждут ответа» — последнее сообщение пришло от
                            // клиента, а чат ещё не закрыт. Закрытый чат важнее:
                            // в нём ничего не ждём.
                            const isClosed = conv.status === 'closed';
                            // Счётчик непрочитанных — только в неоткрытом чате:
                            // прочитано значит прочитано.
                            const hasUnread = conv.id !== activeChatId && conv.unreadCount > 0;
                            // «Ждут ответа» и счётчик вместе не нужны: счётчик
                            // сам говорит, что ответа ждут. Подпись вернётся,
                            // когда сообщения прочитают.
                            const statusLabel = isClosed
                                ? 'Закрыт'
                                : (conv.lastMessageDirection === 'incoming' && !hasUnread
                                    ? 'Ждут ответа'
                                    : '');

                            return (
                                <ListItemButton
                                    key={conv.id}
                                    selected={activeChatId === conv.id}
                                    onClick={() => handleChatClick(conv.id)}
                                    sx={{
                                        borderBottom: '1px solid #f0f0f0',
                                        '&.Mui-selected': { bgcolor: '#e3f2fd' },
                                        py: 1.5 // Две строки: имя со временем и превью со статусом
                                    }}
                                >
                                    {/* СТАТУС ОНЛАЙН (Сверху) */}
                                    <StyledBadge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        variant="dot"
                                        invisible={!conv.contact?.isOnline}
                                    >

                                        <Avatar sx={{ bgcolor: conv.type === 'orion' ? '#9c27b0' : 'primary.main' }}>
                                            {conv.isGroup ? <GroupIcon /> : (conv.contact?.mainName?.[0] || 'U')}
                                        </Avatar>
                                    </StyledBadge>

                                    {/*
                                        Две колонки: слева имя и превью, справа
                                        служебный столбец — дата в первой строке,
                                        счётчик непрочитанных либо статус «Ждут
                                        ответа»/«Закрыт» во второй. Строки
                                        служебного столбца — это те же строки
                                        сетки, что и имя с превью, поэтому вторая
                                        строка встаёт на один уровень с превью
                                        сообщения, а не висит под датой. Правый
                                        край общий: и дата, и вторая строка
                                        прижаты к нему.

                                        Обёртка chat-meta коробку не рисует
                                        (display: contents), её дети — обычные
                                        ячейки сетки; сама нужна для группировки
                                        служебных элементов в разметке и тестах.
                                    */}
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 2,
                                            minWidth: 0,
                                            flexGrow: 1,
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(0, 1fr) auto',
                                            columnGap: 1,
                                            rowGap: 0.25,
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            noWrap
                                            sx={{
                                                gridColumn: 1,
                                                gridRow: 1,
                                                minWidth: 0,
                                                fontWeight: activeChatId === conv.id ? 'bold' : 'medium',
                                            }}
                                        >
                                            {conv.contact?.mainName || 'Неизвестно'}
                                        </Typography>

                                        <Box
                                            component="span"
                                            data-testid="chat-meta"
                                            sx={{ display: 'contents' }}
                                        >
                                            <Typography
                                                variant="caption"
                                                component="span"
                                                data-testid="chat-time"
                                                sx={{
                                                    gridColumn: 2,
                                                    gridRow: 1,
                                                    justifySelf: 'end',
                                                    color: 'text.secondary',
                                                    fontSize: '0.7rem',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {formatChatListTime(conv.lastMessageAt)}
                                            </Typography>

                                            {/* Вторая строка правого столбца, та же
                                                строка сетки, что и превью: либо
                                                счётчик непрочитанных, либо статус.
                                                Вдвоём им тут тесно, да и незачем:
                                                счётчик сам говорит, что ответа ждут,
                                                а подпись вернётся после прочтения. */}
                                            {hasUnread ? (
                                                <Box
                                                    component="span"
                                                    data-testid="unread-count"
                                                    sx={{
                                                        gridColumn: 2,
                                                        gridRow: 2,
                                                        justifySelf: 'end',
                                                        px: 0.6,
                                                        minWidth: 18,
                                                        height: 18,
                                                        borderRadius: '9px',
                                                        bgcolor: 'error.main',
                                                        color: '#fff',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '0.65rem',
                                                        fontWeight: 700,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                                                </Box>
                                            ) : statusLabel ? (
                                                <Box
                                                    component="span"
                                                    data-testid="chat-status"
                                                    sx={{
                                                        gridColumn: 2,
                                                        gridRow: 2,
                                                        justifySelf: 'end',
                                                        px: 0.75,
                                                        borderRadius: 1,
                                                        fontSize: '0.6rem',
                                                        fontWeight: 600,
                                                        lineHeight: 1.6,
                                                        whiteSpace: 'nowrap',
                                                        color: isClosed ? 'text.secondary' : '#c62828',
                                                        bgcolor: isClosed ? '#f0f0f0' : '#fdecea',
                                                    }}
                                                >
                                                    {statusLabel}
                                                </Box>
                                            ) : null}
                                        </Box>
                                        <Box
                                            component="span"
                                            sx={{
                                                gridColumn: 1,
                                                gridRow: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                minWidth: 0,
                                            }}
                                        >
                                            {getChannelIcon(conv.type)}
                                            {/* Канал: Группа, Orion, Telegram, VK, WhatsApp, Avito. */}
                                            <Typography
                                                variant="caption"
                                                component="span"
                                                noWrap
                                                sx={{
                                                    flexShrink: 0,
                                                    color: 'text.secondary',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.02em',
                                                }}
                                            >
                                                {getChannelLabel(conv)}
                                            </Typography>
                                            <Box component="span" sx={{ flexShrink: 0, color: 'text.disabled', fontSize: '0.65rem' }}>
                                                ·
                                            </Box>
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={{ flexGrow: 1, minWidth: 0, color: 'text.secondary', fontSize: '0.75rem' }}
                                            >
                                                {conv.lastMessageText || 'Нет сообщений'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Box>
            )}

            {/* Разделитель списка чатов и переписки: тянется мышью, двойной
                клик возвращает ширину по умолчанию. На мобильном не нужен. */}
            {!isMobile && (
                <Box
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Изменить ширину списка чатов"
                    onMouseDown={handleResizerMouseDown}
                    onDoubleClick={() => setChatListWidth(CHAT_LIST_WIDTH_DEFAULT)}
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        flex: '0 0 auto',
                        width: 6,
                        cursor: 'col-resize',
                        bgcolor: isResizingChatList ? 'primary.main' : 'transparent',
                        transition: isResizingChatList ? 'none' : 'background-color 0.15s',
                        '&:hover': { bgcolor: isResizingChatList ? 'primary.main' : 'action.hover' },
                        // Расширяем хит-зону, чтобы попадать по полосе было легко.
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: -3,
                            right: -3,
                        },
                    }}
                />
            )}

            {(!isMobile || isChatOpen || isCalendarOpen || isSettingsOpen) && (
                <Box sx={{ flexGrow: 1, height: '100%', minWidth: 0 }}>
                    <Routes>
                        <Route path="calendar" element={<CalendarModule />} />
                        <Route path="settings" element={<SettingsPage isMobile={isMobile} />} />
                        <Route path="chat/:id" element={<ChatPage isMobile={isMobile} />} />
                        <Route
                            path="/"
                            element={
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                                    <Typography>Выберите чат для начала общения</Typography>
                                </Box>
                            }
                        />
                    </Routes>
                </Box>
            )}

            {/* Модальное окно выбора пользователя */}
            <UserSelector
                open={isUserSelectorOpen}
                onClose={() => setIsUserSelectorOpen(false)}
                onSelect={handleStartInternalChat}
            />

            <CreateGroupDialog 
                open={isGroupModalOpen} 
                onClose={() => setIsGroupModalOpen(false)}
                onSuccess={handleGroupCreated}
            />

            {/* Глобальный поиск, этап 1: чаты и клиенты. Поиск по сообщениям —
                отдельный этап, поэтому беседы фильтруются по названию и номеру. */}
            <SearchDialog
                open={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                conversations={conversations}
                mode={selectedChatOrgId}
                onOpenChat={(conversationId) => {
                    void handleChatClick(conversationId);
                }}
            />
        </Box>
    );
};

export default DashboardPage;
